<template>
  <DialogHeader v-if="useMobileLayout">
    <template #right>
      <div
        class="font-medium text-green-900 cursor-pointer"
        @click="handleOkay"
      >
        Okay
      </div>
    </template>
    Jurisdictions
  </DialogHeader>
  <DialogContent>
    <div class="absolute w-full top-0 left-0 h-[180px]">
      <img :src="MapGray" class="h-full object-cover" />
      <div
        class="w-full h-[50px] bg-gradient-to-b from-transparent to-white bottom-0 absolute"
      ></div>
    </div>
    <div class="relative z-10 pt-2.5">
      <div class="flex flex-col items-center mb-[34px]">
        <div
          class="font-primary font-semibold text-[26px] leading-[36px] text-gray-800 mb-1.5 text-center"
        >
          Register under national laws
        </div>

        <div
          class="font-secondary text-[14px] leading-[22px] text-gray-800 max-w-[560px] text-center"
        >
          Choose default countries to have your work recognized and registered
          under their copyright laws and applicable legal remedies.
        </div>
      </div>

      <div>
        <div
          class="font-secondary text-[14px] leading-[22px] text-gray-800/50 mb-[14px]"
        >
          Selected jurisdiction to be registered
        </div>

        <div class="grid grid-cols-2 tablet-h:grid-cols-3 items-center gap-2">
          <div
            v-if="dacrOrigin && lockedOriginId !== 'Dacr'"
            class="font-secondary font-medium text-[13px] leading-[15px] text-gray-800 bg-green-200/50 border-2 border-green-500 pl-1.5 pr-3 py-1.5 flex items-center gap-x-2 rounded-[22px] shdw"
          >
            <div
              class="size-[26px] border-2 border-gray-600/15 rounded-full flex items-center justify-center bg-white"
            >
              <Icon name="Dacr" :size="18" />
            </div>

            <div class="flex-1">{{ dacrOrigin.name }}</div>
            <Checkbox :model-value="true" disabled />
          </div>

          <div
            v-if="lockedOrigin"
            class="font-secondary font-medium text-[13px] leading-[15px] text-gray-800 bg-green-200/50 border-2 border-green-500 pl-1.5 pr-3 py-1.5 flex items-center gap-x-2 rounded-[22px] shdw"
          >
            <Avatar
              v-if="lockedOrigin.flag.url"
              :src="lockedOrigin.flag.url"
              :size="26"
            />
            <div
              v-else
              class="size-[26px] border-2 border-gray-600/15 rounded-full flex items-center justify-center bg-white"
            >
              <Icon name="Dacr" :size="18" />
            </div>

            <div class="flex-1">{{ lockedOrigin.name }}</div>
            <Checkbox :model-value="true" disabled />
          </div>

          <div
            v-for="selectedOrigin in selectedOrigins"
            :key="selectedOrigin.id"
            class="font-secondary font-medium text-[13px] leading-[15px] text-gray-800 bg-green-200/50 border-2 border-green-500 pl-1.5 pr-3 py-1.5 flex items-center gap-x-2 rounded-[22px] shdw cursor-pointer"
            @click="toggleOrigin(selectedOrigin.id)"
          >
            <Avatar :src="selectedOrigin.flag.url" :size="26" />
            <div class="flex-1">{{ selectedOrigin.name }}</div>
            <Checkbox
              :model-value="true"
              @update:model-value="toggleOrigin(selectedOrigin.id)"
            />
          </div>
        </div>
      </div>

      <div v-if="availableOrigins.length" class="mt-6">
        <div
          class="font-secondary text-[14px] leading-[22px] text-gray-800/50 mb-[14px]"
        >
          Other jurisdictions
        </div>

        <div class="grid grid-cols-2 tablet-h:grid-cols-3 items-center gap-2">
          <div
            v-for="origin in availableOrigins"
            :key="origin.id"
            class="font-secondary font-medium text-[13px] leading-[15px] text-gray-800 border border-gray-200/50 pl-1.5 pr-3 py-1.5 flex items-center gap-x-2 rounded-[22px] hover:border-gray-300/50 hover:bg-gray-200/13 transition-all shdw cursor-pointer"
            @click="toggleOrigin(origin.id)"
          >
            <Avatar :src="origin.flag.url" :size="26" />
            <div class="flex-1">{{ origin.name }}</div>
            <Checkbox
              @click.stop
              :model-value="false"
              @update:model-value="toggleOrigin(origin.id)"
            />
          </div>
        </div>
      </div>
    </div>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton class="w-1/2 tablet-h:w-auto" primary @click="handleOkay">
      Okay
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { UiButton, Avatar, Checkbox, Icon } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDevice } from '@/shared/composables/useDevice'
import { useDialog } from '@/shared/dialogs/useDialog'
import MapGray from '@/assets/images/MapGray.png'
import { ref, computed, inject } from 'vue'
import { useOriginStore } from '@/shared/stores/origin'
import { graphql } from '@/shared/graphql/router'
import { useQuery } from '@vue/apollo-composable'

const props = defineProps<{
  selectedOriginIds: string[]
  quantity: number
  lockedOriginId: string | null
}>()

const { close, triggerEvent } = useDialog()
const { isMobile } = useDevice()
const originStore = useOriginStore()

const isModal = inject<boolean>('dialogModal', false)
const useMobileLayout = computed(() => isMobile.value && !isModal)

const selectedIds = ref<Set<string>>(new Set(props.selectedOriginIds))

const { result: estimateResult } = useQuery(
  graphql(`
    query EstimateRegistrationCost($input: EstimateRegistrationCostInput!) {
      estimateRegistrationCost(input: $input) {
        total {
          amount
          formatted
        }
        breakdown {
          description
          origin {
            id
            name
            flag {
              url
            }
          }
          total {
            amount
            formatted
          }
          breakdown {
            id
            name
            total {
              amount
              formatted
            }
          }
        }
      }
    }
  `),
  () => ({
    input: {
      origins: [...selectedIds.value],
      quantity: props.quantity,
    },
  }),
  {
    clientId: 'router',
  }
)

const dacrOrigin = computed(() =>
  originStore.origins.find((o) => o.id === 'Dacr')
)

const lockedOrigin = computed(() =>
  props.lockedOriginId
    ? originStore.origins.find((o) => o.id === props.lockedOriginId)
    : null
)

const isLockedId = (id: string) =>
  id === dacrOrigin.value?.id || id === props.lockedOriginId

const selectedOrigins = computed(() =>
  originStore.origins.filter(
    (o) => !isLockedId(o.id) && selectedIds.value.has(o.id)
  )
)

const availableOrigins = computed(() =>
  originStore.origins.filter(
    (o) => !isLockedId(o.id) && !selectedIds.value.has(o.id)
  )
)

const toggleOrigin = (id: string) => {
  if (id === props.lockedOriginId) return
  const next = new Set(selectedIds.value)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  selectedIds.value = next
}

const handleOkay = () => {
  triggerEvent('success', {
    ids: [...selectedIds.value],
    estimatedCost: estimateResult.value?.estimateRegistrationCost ?? null,
  })
  close()
}
</script>

<style scoped>
.shdw {
  box-shadow:
    0 4px 18px 0 rgba(0, 0, 0, 0.04),
    0 0.8px 2.93px 0 rgba(0, 0, 0, 0.02);
}
</style>
